/**
 * The `value` prop for a Semantic UI `Dropdown`/`Form.Dropdown`, as a spreadable object.
 *
 * This app holds "nothing selected" as `null` — `assignTask/index.tsx` seeds and resets
 * `assignee`/`status`/`vendor` to `null`, and the eligibility, CC-practitioner and
 * lab-reading pickers do the same. Semantic declares
 * `value?: string | number | boolean | (string | number | boolean)[]`, which admits
 * neither `null` nor, under `exactOptionalPropertyTypes`, an explicit `undefined`.
 * Omitting the property is the only spelling that declaration accepts.
 *
 * Omitting it is what the runtime already did with `null`. `Dropdown` decides whether a
 * selection exists in `hasValue()`, which is
 * `multiple ? !isEmpty(value) : !isNil(value) && value !== ''`, and lodash `isNil` is
 * true for `null` and `undefined` alike — so both, and an absent prop, render the
 * placeholder rather than a selection.
 *
 * Note this is deliberately NOT applied to a `multiple` dropdown: there `hasValue` uses
 * `isEmpty`, and while `isEmpty(null)` and `isEmpty(undefined)` also agree, a multiple
 * dropdown's value is an array whose absence the caller should state directly.
 */
export type DropdownValue = string | number | boolean | (string | number | boolean)[];

export const dropdownValue = (value: DropdownValue | null | undefined): { value?: DropdownValue } =>
  value === null || value === undefined ? {} : { value };

/**
 * The same treatment for `defaultValue`, which Semantic declares with the identical
 * type and feeds into the identical auto-controlled state.
 *
 * The nine dashboard charts under `components/home/dashboard/lytics` seed their month
 * picker from `dropdownOptions[dropdownOptions.length - 1] && …value`, which is
 * `undefined` before the first data load and on an empty result — the case this omits.
 */
export const dropdownDefaultValue = (
  value: DropdownValue | null | undefined
): { defaultValue?: DropdownValue } => (value === null || value === undefined ? {} : { defaultValue: value });
